import {
  hasActingUserPlatformPermission,
  Permission,
} from '@act/shared/authorization';
import { ActingUserDto, UserDto } from '@act/shared/data-transfer-objects';

/**
 * True when the acting user’s global permissions unioned with the member’s
 * relationship-derived `memberPermissions` includes the given `Permission`.
 *
 * @param actingUser - Acting user from verify
 * @param member - Member DTO from verify or member context
 * @param permission - `Permission` to check on this member
 * @returns Whether the combined member-scoped allow-list grants the permission
 */
export const checkPermissionOnMember = ({
  member,
  actingUser,
  permission,
}: {
  member: UserDto;
  actingUser: ActingUserDto;
  permission: Permission;
}): boolean => {
  const combinedPermissions = [
    ...(actingUser?.userPermissions || []),
    ...(member?.memberPermissions || []),
  ];
  return combinedPermissions.includes(permission);
};

/**
 * True when the acting user has the permission on the global (platform) scope
 * after `/user/verify`: merged `user.permissions` / `user.userPermissions` and IAM
 * `user.iam.platformPermissions` (see {@link hasActingUserPlatformPermission}).
 *
 * @param actingUser - Acting user from verify
 * @param permission - Global `Permission` to check
 * @returns Whether the merged global snapshot grants the permission
 */
export const checkPermissionOnUser = ({
  actingUser,
  permission,
}: {
  actingUser: ActingUserDto;
  permission: Permission;
}): boolean => {
  return hasActingUserPlatformPermission(actingUser, permission);
};
